import {
  Component,
  ElementRef,
  Renderer2,
  OnInit,
  OnDestroy,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { HeaderComponent } from './_shared/header/header.component';
import { FooterComponent } from './_shared/footer/footer.component';
import { MatSidenav, MatSidenavModule } from '@angular/material/sidenav';
import { MatIcon } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { SidenavListComponent } from './_shared/sidenav-list/sidenav-list.component';
import { LayoutService } from './_services/layout.service';
import { Subscription, firstValueFrom } from 'rxjs';
import { Subject } from 'rxjs';
import { environment } from '../environments/environment';
import { ProfileService } from './_services/profile.service';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { LoanAiService } from './loanAiService';
import { PrivacyNoticeDialogComponent } from './_dialog/privacy-notice-dialog/privacy-notice-dialog.component';
@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    HeaderComponent,
    FooterComponent,
    SidenavListComponent,
    MatSidenavModule,
    MatListModule,
    MatDialogModule,
    PrivacyNoticeDialogComponent,
  ],
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  loginStatus: boolean = false;
  // private tenantIdSubscription: Subscription | null = null;
  private readonly _destroying$ = new Subject<void>();
  @ViewChild(MatSidenav) sidenav!: MatSidenav;
  private layoutSubscription: Subscription | undefined;
  isSideNavOpened: boolean = false;
  showSideNav = false;
  profilePhotoUrl: string = './images/avatar.png';
  title = 'ARDEN';
  inactivityTimeout: any;
  tenantLoaded: boolean = false;
  lastActivity: number = Date.now();

  constructor(
    private layoutService: LayoutService,
    private profileService: ProfileService,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private loanAiService: LoanAiService,
    private dialog: MatDialog,
    private router: Router
  ) {
    this.msalService.initialize();
  }

  setupInactivityTimeout() {
    this.inactivityTimeout = setInterval(() => {
      if (Date.now() - this.lastActivity >= 30 * 60 * 1000) {
        this.logout();
      }
    }, 60 * 1000);
  }

  resetInactivityTimer() {
    this.lastActivity = Date.now();
  }

  async ngOnInit() {
    this.layoutSubscription = this.layoutService.showSideNav.subscribe(
      (show) => {
        this.showSideNav = show;
      }
    );

    const checkAndShowPrivacyDialog = () => {
      this.profileService.getPrivacyAcceptedStatus().subscribe({
        next: (res) => {
          if (!res.is_privacy_policy_accepted) {
            const dialogRef = this.dialog.open(PrivacyNoticeDialogComponent, {
              disableClose: true,
              width: '600px',
            });
            dialogRef.afterClosed().subscribe((result) => {
              if (result === 'accept') {
                this.profileService.setPrivacyAcceptedStatus(true).subscribe({
                  next: () => {
                    this.router.navigate(['/']);
                  },
                  error: () => {
                    this.router.navigate(['/']);
                  },
                });
              } else {
                this.profileService.setPrivacyAcceptedStatus(false).subscribe({
                  next: () => {
                    this.profileService.loggedOut();
                    this.router.navigate(['/logout']);
                  },
                  error: () => {
                    this.profileService.loggedOut();
                    this.router.navigate(['/logout']);
                  },
                });
              }
            });
          }
        },
        error: () => {
          const dialogRef = this.dialog.open(PrivacyNoticeDialogComponent, {
            disableClose: true,
            width: '600px',
          });
          dialogRef.afterClosed().subscribe((result) => {
            if (result === 'accept') {
              this.profileService.setPrivacyAcceptedStatus(true).subscribe({
                next: () => {
                  this.router.navigate(['/']);
                },
                error: () => {
                  this.router.navigate(['/']);
                },
              });
            } else {
              this.profileService.setPrivacyAcceptedStatus(false).subscribe({
                next: () => {
                  this.profileService.loggedOut();
                  this.router.navigate(['/logout']);
                },
                error: () => {
                  this.profileService.loggedOut();
                  this.router.navigate(['/logout']);
                },
              });
            }
          });
        },
      });
    };

    this.msalBroadcastService.msalSubject$.subscribe((event: any) => {
      // console.log('MSAL event:', event.eventType);
      if (event.eventType === 'msal:loginSuccess') {
        this.loginStatus = true;
        this.profileService.loggedIn();
        this.getProfilePhoto();
        checkAndShowPrivacyDialog();
        // this.loanAiService.loadTenants();
      } else if (event.eventType === 'msal:logoutSuccess') {
        this.loginStatus = false;
        this.profileService.loggedOut();
      } else if (event.eventType === 'msal:acquireTokenSuccess') {
        //console.log('Unhandled MSAL event:', event);
        if (!this.tenantLoaded) {
          this.loanAiService.loadTenants();
          this.tenantLoaded = true;
        }
      }
    });
    // this.tenantIdSubscription = this.loanAiService.tenantId$.subscribe(
    //   (tenantId) => {
    //     if (tenantId) {
    //       console.log(`In Landing page ${tenantId}`);
    //       checkAndShowPrivacyDialog();
    //     }
    //   }
    // );
    if (this.profileService.isAuthenticated()) {
      this.loginStatus = true;
      this.getProfilePhoto();
      checkAndShowPrivacyDialog();
    }
  }

  ngAfterViewInit(): void {
    this.setupInactivityTimeout();
    ['mousemove', 'keydown', 'mousedown', 'touchstart'].forEach((eventName) => {
      window.addEventListener(eventName, () => this.resetInactivityTimer());
    });
    this.getProfilePhoto();
  }

  getProfilePhoto() {
    this.profileService.getProfilePhoto().subscribe(
      (response) => {
        this.profileService.saveBlobToSessionStorage('prpic', response);
        this.profilePhotoUrl = window.URL.createObjectURL(response);
      },
      (error) => {
        if (this.shouldIgnoreProfilePhotoError(error)) return;
        console.error('Error getting profile photo:', error);
      }
    );
  }

  private shouldIgnoreProfilePhotoError(error: unknown): boolean {
    const details = [
      (error as { errorCode?: string })?.errorCode,
      (error as { name?: string })?.name,
      (error as { message?: string })?.message,
    ]
      .filter(Boolean)
      .join(' ');
    return details.includes('uninitialized_public_client_application');
  }

  onToggleSidenav(data: string) {
    this.isSideNavOpened = data === 'open' ? true : false;
    this.sidenav.toggle();
  }
  getClassList() {
    //'mat-drawer-container mat-sidenav-container mat-drawer-transition' : 'mat-drawer-container mat-sidenav-container mat-drawer-transition sidenav-fix-width'"
    let classList =
      'mat-drawer-container mat-sidenav-container mat-drawer-transition';
    if (!this.isSideNavOpened && this.showSideNav) {
      classList += ' sidenav-fix-width';
    }
    return classList;
  }

  logout() {
    this.profileService.loggedOut();
    // All logout and redirect logic is handled in ProfileService.loggedOut()
    this.msalService
      .logoutRedirect({
        postLogoutRedirectUri:
          environment.msalConfig.auth.postLogoutRedirectUri,
      })
      .subscribe({
        next: () => {},
        error: (err) => {
          // handle error if needed
          console.error('Logout error:', err);
        },
      });
  }

  ngOnDestroy(): void {
    if (this.layoutSubscription) {
      this.layoutSubscription.unsubscribe();
    }
    if (this.inactivityTimeout) {
      clearInterval(this.inactivityTimeout);
    }
    this._destroying$.next();
    this._destroying$.complete();
  }
}
